import { loadAllData } from './data.js';
import {
  lookupTimezone, rankMeetingHours, getRotationOptions, getCurrentOffset, toLocal,
} from './timezone.js';
import {
  renderLocationSlots, setupAutocomplete, renderResults, renderDSTBanner,
  renderHolidayWarnings, renderRecurringAudit, renderNowRail, showToast,
  buildInviteText, MAX_LOCATIONS,
} from './ui.js';
import type { RenderContext } from './ui.js';
import { drawTimeline } from './timeline.js';
import { decodeState, encodeState, buildShareUrl } from './share.js';
import { buildIcs, buildTimeBreakdown } from './ics.js';
import { loadHolidays } from './holidays.js';
import type { HolidayData } from './holidays.js';
import type { AppState, LocationSlot } from './types.js';

const STORAGE_KEY = 'tz-planner-state';

interface SavedState {
  slots: { cityName: string; timezoneId: string; workingStart: number; workingEnd: number; label?: string }[];
  anchorIndex?: number;
  durationMinutes?: number;
}

// === Persistence ===

function saveState(slots: LocationSlot[], anchorIndex: number, durationMinutes: number): void {
  const data: SavedState = {
    slots: slots.map(s => ({
      cityName: s.city.name,
      timezoneId: s.timezone.id,
      workingStart: s.workingStart,
      workingEnd: s.workingEnd,
      label: s.label === s.city.name ? undefined : s.label,
    })),
    anchorIndex,
    durationMinutes,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage full or unavailable — state is still in the URL
  }
}

function loadSavedState(data: AppState): { slots: LocationSlot[]; anchorIndex: number; durationMinutes: number } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const saved: SavedState = JSON.parse(raw);
    if (!Array.isArray(saved.slots) || saved.slots.length === 0) return null;

    const slots: LocationSlot[] = [];
    for (const s of saved.slots) {
      const city = data.cities.find(c => c.name === s.cityName && c.timezone === s.timezoneId);
      const tz = lookupTimezone(data.timezones, s.timezoneId);
      if (city && tz) {
        slots.push({
          city,
          timezone: tz,
          label: s.label || city.name,
          workingStart: s.workingStart ?? 9,
          workingEnd: s.workingEnd ?? 17,
        });
      }
    }
    if (slots.length === 0) return null;

    const anchorIndex = saved.anchorIndex ?? 0;
    return {
      slots,
      anchorIndex: anchorIndex >= 0 && anchorIndex < slots.length ? anchorIndex : 0,
      durationMinutes: saved.durationMinutes ?? 30,
    };
  } catch {
    return null;
  }
}

function buildDefaults(data: AppState): LocationSlot[] {
  const defaults = [
    { name: 'San Francisco', timezoneId: 'America/Los_Angeles' },
    { name: 'London', timezoneId: 'Europe/London' },
  ];
  const slots: LocationSlot[] = [];
  for (const dc of defaults) {
    const city = data.cities.find(c => c.name === dc.name && c.timezone === dc.timezoneId);
    const tz = lookupTimezone(data.timezones, dc.timezoneId);
    if (city && tz) {
      slots.push({ city, timezone: tz, label: city.name, workingStart: 9, workingEnd: 17 });
    }
  }
  return slots;
}

// === Date helpers ===

function parseDateInput(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  return new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, 12));
}

function toDateInputValue(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function isSameUTCDay(a: Date, b: Date): boolean {
  return a.getUTCFullYear() === b.getUTCFullYear()
    && a.getUTCMonth() === b.getUTCMonth()
    && a.getUTCDate() === b.getUTCDate();
}

// === Main ===

async function main(): Promise<void> {
  try {
    const data = await loadAllData();

    const yearEl = document.getElementById('current-year');
    const lastUpdatedEl = document.getElementById('last-updated');
    if (yearEl) yearEl.textContent = String(new Date().getFullYear());
    if (lastUpdatedEl) lastUpdatedEl.textContent = data.meta.last_updated;

    setupThemeToggle();

    // --- state ---
    // A shared link always wins over local state: the point of opening one is
    // to see what the sender saw.
    const shared = decodeState(window.location.hash, data);
    const saved = shared ? null : loadSavedState(data);

    let slots: LocationSlot[] = shared?.slots ?? saved?.slots ?? buildDefaults(data);
    let anchorIndex = shared?.anchorIndex ?? saved?.anchorIndex ?? 0;
    let durationMinutes = saved?.durationMinutes ?? 30;
    let selectedDate: Date | null = shared?.date ? parseDateInput(shared.date) : null;
    let holidayData: HolidayData | null = null;

    // --- elements ---
    const locationSlotsEl = document.getElementById('location-slots')!;
    const resultsEl = document.getElementById('meeting-times')!;
    const dstBannerEl = document.getElementById('dst-banner')!;
    const holidayEl = document.getElementById('holiday-warnings')!;
    const auditEl = document.getElementById('recurring-audit')!;
    const addBtn = document.getElementById('add-location') as HTMLButtonElement;
    const timelineCanvas = document.getElementById('timeline-canvas') as HTMLCanvasElement;
    const dateInput = document.getElementById('plan-date') as HTMLInputElement;
    const todayBtn = document.getElementById('reset-date') as HTMLButtonElement;
    const anchorSelect = document.getElementById('anchor-select') as HTMLSelectElement;
    const durationSelect = document.getElementById('duration-select') as HTMLSelectElement;

    // --- autocomplete ---
    const autocompleteWrapper = document.createElement('div');
    autocompleteWrapper.className = 'autocomplete-wrapper';
    autocompleteWrapper.innerHTML = `
      <input type="text" id="city-search" placeholder="Search for a city..." autocomplete="off">
      <div class="autocomplete-dropdown" id="city-dropdown"></div>
    `;
    addBtn.parentNode!.insertBefore(autocompleteWrapper, addBtn);

    const searchInput = autocompleteWrapper.querySelector('#city-search') as HTMLInputElement;
    const dropdown = autocompleteWrapper.querySelector('#city-dropdown') as HTMLElement;

    addBtn.addEventListener('click', () => {
      autocompleteWrapper.classList.toggle('active');
      if (autocompleteWrapper.classList.contains('active')) searchInput.focus();
    });

    setupAutocomplete(autocompleteWrapper, searchInput, dropdown, data.cities, (city) => {
      const tz = lookupTimezone(data.timezones, city.timezone);
      if (!tz) {
        showToast(`Timezone not found for ${city.timezone}`);
        return;
      }
      if (slots.some(s => s.city.name === city.name && s.city.timezone === city.timezone)) {
        showToast(`${city.name} is already added`);
        return;
      }
      if (slots.length >= MAX_LOCATIONS) {
        showToast(`Maximum ${MAX_LOCATIONS} locations`);
        return;
      }
      slots.push({ city, timezone: tz, label: city.name, workingStart: 9, workingEnd: 17 });
      autocompleteWrapper.classList.remove('active');
      updateAll();
    });

    // --- context builder ---
    function buildContext(): RenderContext {
      const now = new Date();
      const at = selectedDate ?? now;
      const date = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()));
      return {
        slots,
        at,
        date,
        anchorIndex: Math.min(anchorIndex, Math.max(0, slots.length - 1)),
        durationMinutes,
        isToday: isSameUTCDay(at, now),
      };
    }

    // --- controls ---
    function syncControls(ctx: RenderContext): void {
      dateInput.value = toDateInputValue(ctx.date);
      todayBtn.hidden = ctx.isToday;

      anchorSelect.innerHTML = slots
        .map((s, i) => `<option value="${i}"${i === ctx.anchorIndex ? ' selected' : ''}>${escapeAttr(s.label)}</option>`)
        .join('');

      durationSelect.value = String(durationMinutes);
    }

    dateInput.addEventListener('change', () => {
      const parsed = parseDateInput(dateInput.value);
      const now = new Date();
      selectedDate = parsed && !isSameUTCDay(parsed, now) ? parsed : null;
      updateAll();
    });

    todayBtn.addEventListener('click', () => {
      selectedDate = null;
      updateAll();
    });

    anchorSelect.addEventListener('change', () => {
      anchorIndex = parseInt(anchorSelect.value, 10) || 0;
      updateAll();
    });

    durationSelect.addEventListener('change', () => {
      durationMinutes = parseInt(durationSelect.value, 10) || 30;
      updateAll();
    });

    // --- actions ---
    function downloadIcs(ctx: RenderContext, utcHour: number, recurring: boolean): void {
      const ics = buildIcs({
        slots: ctx.slots,
        utcHour,
        date: ctx.date,
        durationMinutes: ctx.durationMinutes,
        title: `Meeting — ${ctx.slots.map(s => s.city.name).join(' / ')}`,
        anchorIndex: ctx.anchorIndex,
        recurring,
      });
      const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `meeting-${toDateInputValue(ctx.date)}-${String(utcHour).padStart(2, '0')}00utc.ics`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('Calendar invite downloaded');
    }

    function copyLine(ctx: RenderContext, utcHour: number): void {
      copyText(buildInviteText(ctx, utcHour), 'Invite line copied');
    }

    function copyText(text: string, message: string): void {
      navigator.clipboard.writeText(text)
        .then(() => showToast(message))
        .catch(() => showToast('Failed to copy. Please try again.'));
    }

    async function copyTimelineImage(): Promise<void> {
      const blob: Blob | null = await new Promise(resolve => timelineCanvas.toBlob(resolve, 'image/png'));
      if (!blob) {
        showToast('Could not render the timeline image');
        return;
      }
      // Clipboard image writing is not universally available; fall back to a
      // download so the picture is still shareable either way.
      try {
        if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) throw new Error('unsupported');
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        showToast('Timeline image copied');
      } catch {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'meeting-timeline.png';
        a.click();
        URL.revokeObjectURL(url);
        showToast('Timeline image downloaded');
      }
    }

    document.getElementById('copy-image')?.addEventListener('click', () => void copyTimelineImage());
    document.getElementById('copy-link')?.addEventListener('click', () => {
      copyText(buildShareUrl(slots, selectedDate, anchorIndex), 'Share link copied');
    });

    // --- render ---
    function updateAll(): void {
      const ctx = buildContext();
      anchorIndex = ctx.anchorIndex;

      syncControls(ctx);
      renderNowRail(ctx);

      renderLocationSlots(
        locationSlotsEl,
        ctx,
        (index) => {
          slots.splice(index, 1);
          if (anchorIndex >= slots.length) anchorIndex = Math.max(0, slots.length - 1);
          updateAll();
        },
        (index, start, end) => {
          const slot = slots[index];
          if (slot) {
            slot.workingStart = start;
            slot.workingEnd = end;
            updateAll();
          }
        },
        (index, label) => {
          const slot = slots[index];
          if (slot) {
            slot.label = label || slot.city.name;
            updateAll();
          }
        }
      );

      const ranked = rankMeetingHours(slots, ctx.at);
      const rotation = getRotationOptions(slots, ctx.at);
      const bestHour = ranked[0]?.utcHour;

      drawTimeline(timelineCanvas, { slots, at: ctx.at, isToday: ctx.isToday, bestHour });

      renderResults(resultsEl, ctx, ranked, rotation, {
        onDownloadIcs: (hour, recurring) => downloadIcs(ctx, hour, recurring),
        onCopyLine: (hour) => copyLine(ctx, hour),
      });

      renderDSTBanner(dstBannerEl, ctx);
      renderHolidayWarnings(holidayEl, ctx, holidayData, bestHour ?? 12);

      // The recurring audit follows the anchor's local time at the recommended hour.
      const anchor = slots[ctx.anchorIndex];
      if (anchor && bestHour !== undefined) {
        const anchorOffset = getCurrentOffset(anchor.timezone, ctx.at);
        const anchorLocalHour = Math.round(toLocal(bestHour, anchorOffset).minutes / 60);
        renderRecurringAudit(auditEl, ctx, anchorLocalHour);
      } else {
        auditEl.innerHTML = '';
      }

      // Whole-result copy buttons are re-created on every render.
      document.getElementById('copy-times-btn')?.addEventListener('click', () => {
        const lines = ['Meeting time options:', ''];
        const shortlist = ranked.filter(r => r.fullOverlap);
        for (const r of (shortlist.length > 0 ? shortlist : ranked.slice(0, 5))) {
          lines.push(buildTimeBreakdown(slots, r.utcHour, ctx.date), '');
        }
        copyText(lines.join('\n'), 'Times copied to clipboard');
      });
      document.getElementById('copy-invite-btn')?.addEventListener('click', () => {
        if (bestHour !== undefined) copyLine(ctx, bestHour);
      });

      // Persist to both localStorage and the URL, so the current view is always
      // linkable without an explicit "save" step.
      saveState(slots, anchorIndex, durationMinutes);
      history.replaceState(null, '', encodeState(slots, selectedDate, anchorIndex));
    }

    updateAll();

    // Holidays are non-blocking: the planner is fully usable before they land.
    void loadHolidays().then(h => {
      if (!h) return;
      holidayData = h;
      updateAll();
    });

    // Keep the live clocks moving, but only while showing today.
    setInterval(() => {
      if (buildContext().isToday) updateAll();
    }, 30000);

    let resizeTimer: ReturnType<typeof setTimeout>;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const ctx = buildContext();
        const bestHour = rankMeetingHours(slots, ctx.at)[0]?.utcHour;
        drawTimeline(timelineCanvas, { slots, at: ctx.at, isToday: ctx.isToday, bestHour });
      }, 150);
    });

    // Someone pasting a different share link into the same tab should see it.
    window.addEventListener('hashchange', () => {
      const next = decodeState(window.location.hash, data);
      if (!next) return;
      slots = next.slots;
      anchorIndex = next.anchorIndex;
      selectedDate = next.date ? parseDateInput(next.date) : null;
      updateAll();
    });
  } catch (err) {
    console.error('Failed to initialize app:', err);
    const planner = document.getElementById('planner');
    if (planner) {
      planner.insertAdjacentHTML(
        'afterbegin',
        '<p class="error">Unable to load timezone data. Please check your connection and try again.</p>'
      );
    }
  }
}

function escapeAttr(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Theme follows the OS unless the visitor has explicitly chosen one, so no
 * data-theme attribute exists in the default case — the CSS media query handles
 * it. That means the current theme has to be read from the media query, not
 * assumed.
 */
function setupThemeToggle(): void {
  const btn = document.getElementById('theme-toggle');
  if (!btn) return;

  btn.addEventListener('click', () => {
    const root = document.documentElement;
    const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const current = root.getAttribute('data-theme') || (systemDark ? 'dark' : 'light');
    const next = current === 'dark' ? 'light' : 'dark';
    root.setAttribute('data-theme', next);
    localStorage.setItem('theme', next);
    // The canvas palette is read from CSS custom properties at draw time, so it
    // has to be repainted after the theme changes.
    window.dispatchEvent(new Event('resize'));
  });
}

main();
